import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, LogOut, Menu, School, X } from 'lucide-react';
import { navigationForRole } from '../app/navigation';
import type { RadarProfile, SchoolOption } from '../types/app';
import './shell.css';

type Props = {
  active: string;
  onNavigate: (id: string) => void;
  onLogout: () => void;
  children: ReactNode;
  profile: RadarProfile;
  schools: SchoolOption[];
  selectedSchoolId: string;
  onSchoolChange: (schoolId: string) => void;
};

const roleLabels: Record<string, string> = {
  admin: 'Administrador',
  coordinator_literacy: 'Coordenação · Alfabetização',
  coordinator_fundamental: 'Coordenação · Fundamental',
  teacher: 'Professor(a)',
};

export function AppShell({ active, onNavigate, onLogout, children, profile, schools, selectedSchoolId, onSchoolChange }: Props) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAdmin = profile.role === 'admin';
  const currentSchool = schools.find(s => s.id === selectedSchoolId) || schools[0];
  const items = navigationForRole(profile.role);
  const initials = profile.name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase() || 'U';

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  const navigate = (id: string) => {
    onNavigate(id);
    setMobileMenuOpen(false);
  };

  const logout = () => {
    setMobileMenuOpen(false);
    onLogout();
  };

  return (
    <div className="shell">
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`} aria-hidden={!mobileMenuOpen ? undefined : false}>
        <div className="side-head">
          <img src="/assets/radar-saeb-logo-sidebar.svg" alt="Radar SAEB Simulados" />
          <button className="mobile-drawer-close" type="button" onClick={() => setMobileMenuOpen(false)} aria-label="Fechar menu"><X size={22}/></button>
        </div>
        <nav className="side-nav" aria-label="Navegação principal">
          {items.map(item => {
            const Icon = item.icon;
            return <button key={item.id} className={active === item.id ? 'active' : ''} onClick={() => navigate(item.id)}><Icon size={19}/><span>{item.label}</span></button>;
          })}
        </nav>
        <div className="side-footer">
          <div className="municipal-brand"><img src="/assets/prefeitura-tres-lagoas.png" alt="Prefeitura de Três Lagoas" /></div>
          <button className="logout" onClick={logout}><LogOut size={18}/>Sair</button>
        </div>
      </aside>

      <button className={`mobile-backdrop ${mobileMenuOpen ? 'show' : ''}`} type="button" aria-label="Fechar menu" onClick={() => setMobileMenuOpen(false)} />

      <section className="main">
        <header className="topbar">
          <button className="mobile-menu-button" type="button" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menu" aria-expanded={mobileMenuOpen}><Menu size={23}/></button>
          <div className="app-context"><strong>Radar SAEB</strong><small>SEMED · Três Lagoas</small></div>

          {isAdmin && schools.length > 0 && <div className="school-switcher-wrap">
            <label htmlFor="school-switcher"><School size={17}/><span>Escola em visualização</span></label>
            <div className="school-switcher-control">
              <select id="school-switcher" value={selectedSchoolId} onChange={e => onSchoolChange(e.target.value)} aria-label="Selecionar escola">
                {schools.map(s => <option value={s.id} key={s.id}>{s.name}</option>)}
              </select>
              <ChevronDown size={18} aria-hidden="true"/>
            </div>
          </div>}

          {!isAdmin && currentSchool && <div className="school-fixed"><School size={17}/><div><span>Escola</span><b>{currentSchool.short_name || currentSchool.name}</b></div></div>}

          <div className="user-pill"><span>{initials}</span><div><b>{profile.name}</b><small>{roleLabels[profile.role] || profile.role}</small></div></div>
        </header>
        <main className="content">{children}</main>
      </section>
    </div>
  );
}
